import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot } from '@angular/router';
import { LoggerService } from '@shared/core/services/logger.service';
import { UserService } from '@shared/core/services/user.service';
import { UserRole } from '@shared/core/types';

@Injectable({
  providedIn: 'root',
})
export class PermissionGuard {
  constructor(private userService: UserService, private log: LoggerService, private router: Router) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.hasPermission(next.data.roles, state.url, next.data.redirectTo);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.hasPermission(childRoute.data.roles, state.url, childRoute.data.redirectTo);
  }

  canLoad(route: Route): Observable<boolean> {
    return this.hasPermission(route.data.roles, route.path, route.data.redirectTo);
  }

  private hasPermission(roles: UserRole[], currentUrl: string, redirectTo?: string): Observable<boolean> {
    if (!Array.isArray(roles)) {
      this.log.warn('There are no roles for `PermissionGuard`');
      this.redirect(currentUrl, redirectTo);

      return of(false);
    }

    const isAllowed = roles.some(perm => this.userService.hasRole(perm));
    if (isAllowed) {
      return of(true);
    }

    this.redirect(currentUrl, redirectTo);

    this.log.info('Access denied by `PermissionGuard`');

    return of(false);
  }

  private redirect(url: string, redirectTo?: string): void {
    if (redirectTo) {
      this.router.navigate([redirectTo]);
    } else if (url !== '/') {
      this.router.navigate(['/']);
    }
  }
}
